import { createTheme, type ThemeOptions } from '@mui/material/styles';
import { colors, type PaletteMode } from './palette';
import './expanded-theme';

const statusCardBase = {
    display: 'block',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '4px 8px',
    margin: '4px',
    borderRadius: '4px',
    maxWidth: 'fit-content',
    fontSize: '12px',
};

export const buildThemeOptions = (mode: PaletteMode): ThemeOptions => {
    const palette = colors(mode);

    return {
        shadows: [
            'none',
            '0px 1px 4px 0px rgba(0, 0, 0, 0.15), 0px 2px 6px 1px rgba(0, 0, 0, 0.1)',
            '0px 2px 6px 0px rgba(0, 0, 0, 0.15), 0px 4px 12px 2px rgba(0, 0, 0, 0.1)',
            '0px 6px 12px 3px rgba(0, 0, 0, 0.1), 0px 2px 6px 1px rgba(0, 0, 0, 0.15)',
            '0px 8px 16px 4px rgba(0, 0, 0, 0.1), 0px 4px 8px 2px rgba(0, 0, 0, 0.15)',
            '0px 10px 20px 5px rgba(0, 0, 0, 0.1), 0px 6px 12px 3px rgba(0, 0, 0, 0.15)',
            '0px 12px 24px 6px rgba(0, 0, 0, 0.1), 0px 8px 16px 4px rgba(0, 0, 0, 0.15)',
            '0px 14px 28px 7px rgba(0, 0, 0, 0.1), 0px 10px 20px 5px rgba(0, 0, 0, 0.15)',
            '0px 16px 32px 8px rgba(0, 0, 0, 0.1), 0px 12px 24px 6px rgba(0, 0, 0, 0.15)',
            'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none',
            'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none',
        ] as ThemeOptions['shadows'],
        palette: {
            mode,
            common: { white: 'white' },
            secondary: {
                light: palette.blue[300],
                main: palette.blue[700],
            },
            background: mode === 'dark'
                ? { default: '#010319' }
                : { default: '#F1F3F9', paper: '#FCFCFD' },
            success: { main: '#2FE047' },
            primary: palette.primary,
            green: palette.green,
            yellow: palette.yellow,
            red: palette.red,
            blue: palette.blue,
            twilightBlue: palette.twilightBlue,
            grey: palette.grey,
            white: palette.white,
            black: palette.black,
        },
        typography: {
            fontFamily: ['Roboto', 'sans-serif'].join(','),
            fontSize: 12,
            subtitle2: {
                fontSize: 14,
                fontFamily: ['Roboto', 'sans-serif'].join(','),
                fontWeight: 500,
            },
            subtitle1: {
                fontSize: 16,
                fontFamily: ['Roboto', 'sans-serif'].join(','),
                fontWeight: 400,
            },
            h1: {
                fontFamily: ['Roboto', 'sans-serif'].join(','),
                fontSize: 28,
                fontWeight: 500,
            },
            h2: { fontFamily: ['Roboto', 'sans-serif'].join(','), fontSize: 32 },
            h3: { fontFamily: ['Roboto', 'sans-serif'].join(','), fontSize: 24 },
            h4: { fontFamily: ['Roboto', 'sans-serif'].join(','), fontSize: 20 },
            h5: { fontFamily: ['Roboto', 'sans-serif'].join(','), fontSize: 16 },
            h6: { fontFamily: ['Roboto', 'sans-serif'].join(','), fontSize: 14 },
        },
        components: {
            MuiSvgIcon: {
                styleOverrides: { root: { color: palette.blue[500] } },
            },
            MuiButton: {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    contained: {
                        '& .MuiButton-startIcon .MuiSvgIcon-root, & .MuiButton-endIcon .MuiSvgIcon-root': {
                            color: 'inherit',
                        },
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    colorPrimary: {
                        background: palette.white,
                        borderBottom: '1px solid',
                        borderBottomColor: palette.grey[40],
                    },
                },
                defaultProps: { position: 'static', elevation: 0 },
            },
            MuiCard: {
                defaultProps: { elevation: 0 },
                styleOverrides: { root: { borderRadius: '16px', padding: '20px' } },
                variants: [
                    { props: { variant: 'OPEN' as never }, style: { ...statusCardBase, background: palette.blue[50], color: palette.blue.main } },
                    { props: { variant: 'IN PROGRESS' as never }, style: { ...statusCardBase, background: palette.blue[50], color: palette.blue.main } },
                    { props: { variant: 'IN_PROGRESS' as never }, style: { ...statusCardBase, background: palette.blue[50], color: palette.blue.main } },
                    { props: { variant: 'PENDING' as never }, style: { ...statusCardBase, background: palette.grey[40], color: palette.grey.main } },
                    { props: { variant: 'ACCEPTED' as never }, style: { ...statusCardBase, background: palette.green[40], color: palette.green[600] } },
                    { props: { variant: 'DECLINED' as never }, style: { ...statusCardBase, background: palette.red[40], color: palette.red[600] } },
                    { props: { variant: 'CANCELED' as never }, style: { ...statusCardBase, background: palette.grey[40], color: palette.grey[400] } },
                    { props: { variant: 'COMPLETED' as never }, style: { ...statusCardBase, background: palette.green[500], color: palette.green[30] } },
                    { props: { variant: 'INVOICED' as never }, style: { ...statusCardBase, background: palette.blue[500], color: palette.blue[30] } },
                    { props: { variant: 'CLOSED' as never }, style: { ...statusCardBase, background: palette.green[500], color: palette.green[30] } },
                    { props: { variant: 'SUPPLEMENT_PENDING' as never }, style: { ...statusCardBase, background: palette.yellow[30], color: palette.yellow[900] } },
                    { props: { variant: 'REWORK' as never }, style: { ...statusCardBase, background: palette.grey[30], color: palette.blue[900] } },
                ],
            },
            MuiPaper: {
                styleOverrides: {
                    elevation1: { boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.1)' },
                },
            },
            MuiTextField: {
                defaultProps: { color: 'secondary', variant: 'standard' },
                styleOverrides: {
                    root: {
                        '& .MuiFormHelperText-root': {
                            position: 'absolute',
                            bottom: -20,
                        },
                        '& .MuiInputLabel-root': {
                            color: palette.grey[500],
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                            color: palette.blue.main,
                        },
                        '& .MuiInput-underline:before': {
                            borderBottom: `1px solid ${palette.grey[100]}`,
                        },
                        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                            borderBottom: `1px solid ${palette.grey[200]}`,
                        },
                        '& .MuiInput-underline:after': {
                            borderBottom: `2px solid ${palette.blue.main}`,
                        },
                    },
                },
            },
            MuiInput: {
                styleOverrides: {
                    underline: {
                        '&:before': {
                            borderBottomColor: palette.grey[100],
                        },
                        '&:hover:not(.Mui-disabled):before': {
                            borderBottom: `1px solid ${palette.grey[200]}`,
                        },
                        '&:after': {
                            borderBottom: `2px solid ${palette.blue.main}`,
                        },
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    filled: {
                        '&:not(.MuiInputLabel-shrink)': {
                            transform: 'translate(12px, 20px) scale(1)',
                        },
                    },
                },
            },
            MuiMenuItem: { defaultProps: { disableRipple: true } },
        },
    };
};

export const buildTheme = (mode: PaletteMode = 'light') => createTheme(buildThemeOptions(mode));
